import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../contexts/ThemeContext';

const SettingsSection = styled.section`
  padding: 4rem 2rem;
  min-height: 70vh;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
`;

const SettingsContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const SettingsTitle = styled.h1`
  font-size: 2.5rem;
  color: ${({ theme }) => theme.colors.accent};
  text-align: center;
  margin-bottom: 3rem;
  text-decoration: underline;
  text-underline-offset: 8px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const SettingsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const SettingCard = styled.div`
  background: ${({ theme }) => theme.colors.panelBackground};
  border: 1px solid ${({ theme }) => theme.colors.panelBorder};
  border-radius: 16px;
  padding: 2rem;
  box-shadow: ${({ theme }) => theme.colors.panelShadow};
`;

const SettingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const SettingInfo = styled.div`
  color: ${({ theme }) => theme.colors.text};
`;

const SettingTitle = styled.h3`
  color: ${({ theme }) => theme.colors.accent};
  font-size: 1.3rem;
  margin-bottom: 0.5rem;
`;

const SettingDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ToggleSwitch = styled.button<{ isActive: boolean }>`
  width: 60px;
  height: 30px;
  background: ${({ isActive, theme }) =>
    isActive ? theme.colors.accent : theme.colors.panelBorder};
  border: none;
  border-radius: 15px;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    width: 26px;
    height: 26px;
    background: ${({ theme }) => theme.colors.white};
    border-radius: 50%;
    top: 2px;
    left: ${({ isActive }) => isActive ? '32px' : '2px'};
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  &:hover {
    background: ${({ isActive, theme }) =>
      isActive ? theme.colors.primary : theme.colors.cardBorder};
  }
`;

const ToggleLabel = styled.span`
  color: ${({ theme }) => theme.colors.text};
  font-weight: 500;
`;

const ComingSoonSection = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${({ theme }) => theme.colors.text};
`;

const ComingSoonTitle = styled.h3`
  color: ${({ theme }) => theme.colors.accent};
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1.5rem 0;
  text-align: left;
`;

const FeatureItem = styled.li`
  background: ${({ theme }) => theme.colors.panelBackground};
  border: 1px solid ${({ theme }) => theme.colors.panelBorder};
  margin-bottom: 0.5rem;
  padding: 1rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &::before {
    content: '⚙️';
    font-size: 1.2rem;
  }
`;

const ThemePreview = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 1rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const PreviewCard = styled.div<{ variant: 'light' | 'dark'; active?: boolean }>`
  padding: 1rem;
  border-radius: 8px;
  background: ${({ variant }) =>
    variant === 'dark'
      ? 'linear-gradient(135deg, #0f172a 0%, #020617 100%)'
      : 'linear-gradient(135deg, #eef2ff 0%, #e2e8f0 100%)'};
  color: ${({ variant }) => (variant === 'dark' ? '#F5F5F5' : '#1F2937')};
  text-align: center;
  font-size: 0.9rem;
  border: ${({ active, theme }) => (active ? `2px solid ${theme.colors.accent}` : '2px solid transparent')};
`;

const Settings: React.FC = () => {
  const { isDarkMode, toggleTheme } = useTheme();

  const upcomingFeatures = [
    'Sound notification settings',
    'Video autoplay preferences',
    'Email notification preferences',
    'Language selection',
    'Accessibility options',
    'Data export options'
  ];

  return (
    <SettingsSection>
      <SettingsContainer>
        <SettingsTitle>Settings</SettingsTitle>
        
        <SettingsGrid>
          <SettingCard>
            <SettingHeader>
              <SettingInfo>
                <SettingTitle>🎨 Appearance</SettingTitle>
                <SettingDescription>
                  Choose between light and dark theme for better viewing experience.
                </SettingDescription>
              </SettingInfo>
              <ToggleContainer>
                <ToggleLabel>Light</ToggleLabel>
                <ToggleSwitch
                  isActive={isDarkMode}
                  onClick={toggleTheme}
                  aria-label={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
                />
                <ToggleLabel>Dark</ToggleLabel>
              </ToggleContainer>
            </SettingHeader>
            
            <ThemePreview>
              <PreviewCard variant="light" active={!isDarkMode}>
                Light Theme Preview
                <br />
                <small>Purple gradient with light background</small>
              </PreviewCard>
              <PreviewCard variant="dark" active={isDarkMode}>
                Dark Theme Preview
                <br />
                <small>Deep purple gradient with dark background</small>
              </PreviewCard>
            </ThemePreview>
          </SettingCard>

          <SettingCard>
            <ComingSoonSection>
              <ComingSoonTitle>🚀 More Settings Coming Soon</ComingSoonTitle>
              <p>
                We're working on additional customization options to enhance your 
                experience with The Men's Well website.
              </p>
              
              <FeatureList>
                {upcomingFeatures.map((feature, index) => (
                  <FeatureItem key={index}>{feature}</FeatureItem>
                ))}
              </FeatureList>
              
              <p style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                Have suggestions for new settings? Contact us through our contact page!
              </p>
            </ComingSoonSection>
          </SettingCard>
        </SettingsGrid>
      </SettingsContainer>
    </SettingsSection>
  );
};

export default Settings;
